import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Server, 
  Database, 
  Cloud, 
  GitBranch,
  Activity,
  TrendingUp 
} from 'lucide-react';
import { appsService, gitService, databaseService, minioService } from '../services/api';
import toast from 'react-hot-toast';

function Dashboard() {
  const [stats, setStats] = useState({
    apps: 0,
    databases: 0,
    buckets: 0,
    commits: 0
  });
  const [recentApps, setRecentApps] = useState([]);
  const [gitStatus, setGitStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const [appsData, databasesData, bucketsData, gitLogData, gitStatusData] = await Promise.allSettled([
        appsService.getAll(),
        databaseService.listDatabases(),
        minioService.listBuckets(),
        gitService.getLog(5),
        gitService.getStatus()
      ]);

      // Update stats
      setStats({
        apps: appsData.status === 'fulfilled' ? appsData.value.count : 0,
        databases: databasesData.status === 'fulfilled' ? databasesData.value.databases.length : 0,
        buckets: bucketsData.status === 'fulfilled' ? bucketsData.value.buckets.length : 0,
        commits: gitLogData.status === 'fulfilled' ? gitLogData.value.commits.length : 0
      });

      // Set recent apps
      if (appsData.status === 'fulfilled') {
        setRecentApps(appsData.value.apps.slice(0, 5));
      }

      // Set git status
      if (gitStatusData.status === 'fulfilled') {
        setGitStatus(gitStatusData.value.status);
      }
      
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error('Dashboard load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className="text-lg font-medium text-gray-900">{value}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Overview of your deployed applications and infrastructure
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link
            to="/apps/new"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            New App
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard 
          title="Applications" 
          value={stats.apps} 
          icon={Server} 
          color="blue" 
        />
        <StatCard 
          title="Databases" 
          value={stats.databases} 
          icon={Database} 
          color="green" 
        />
        <StatCard 
          title="Storage Buckets" 
          value={stats.buckets} 
          icon={Cloud} 
          color="purple" 
        />
        <StatCard 
          title="Recent Commits" 
          value={stats.commits} 
          icon={GitBranch} 
          color="orange" 
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Apps */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Recent Applications
              </h3>
              <Link
                to="/apps"
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                View all
              </Link>
            </div>
          </div>
          <ul role="list" className="divide-y divide-gray-200">
            {recentApps.length > 0 ? (
              recentApps.map((app) => (
                <li key={app.name} className="px-4 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <Activity className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {app.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {app.host}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {app.replicas} replica{app.replicas !== 1 ? 's' : ''}
                    </div>
                  </div>
                </li>
              ))
            ) : (
              <li className="px-4 py-8 text-center">
                <div className="text-gray-500">
                  No applications found
                </div>
              </li>
            )}
          </ul>
        </div>

        {/* Git Status */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Repository Status
              </h3>
              <Link
                to="/git"
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                Manage
              </Link>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {gitStatus ? (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Current Branch:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {gitStatus.branch}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Modified Files:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {gitStatus.modified?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Staged Files:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {gitStatus.staged?.length || 0}
                  </span>
                </div>
                {(gitStatus.ahead > 0 || gitStatus.behind > 0) && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Sync Status:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {gitStatus.ahead > 0 && `+${gitStatus.ahead} `}
                      {gitStatus.behind > 0 && `-${gitStatus.behind}`}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-500 text-center">
                Unable to load git status
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;